import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { rolesApi } from '../../roles/rolesApi';
import { usersApi } from '../usersApi';
import type { RoleListItem } from '../../roles/roleTypes';
import type { UserDetail } from '../userTypes';

/**
 * The Role card's editor — the role assignment, and nothing else.
 *
 * A role change alters what the account may do, so the server re-seeds its permission grants
 * from the new roles and signs every session out. The confirm says so: this is not a
 * cosmetic edit and the user should know before ticking.
 */
export function EditUserRoleModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!user) return;

    setRoleId(user.roleId ?? '');
    setConfirmed(false);
    setError(null);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();
    setRolesLoading(true);

    rolesApi
      .list({ status: 'active', pageSize: 100 }, controller.signal)
      .then((result) => setRoles(result.items))
      .catch(() => {
        if (!controller.signal.aborted) setRoles([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setRolesLoading(false);
      });

    return () => controller.abort();
  }, [user]);

  if (!user) return null;

  const valid = roleId.length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!user || !canSave) return;

    setSaving(true);
    setError(null);

    try {
      await usersApi.update(user.id, { alias: user.alias, roleId });
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not update the roles.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'edit-user-role',
        fieldIds: { roleId: 'edit-user-role-picker' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit role"
      size="md"
      id="edit-user-role-modal"
      /*
        Picks a real role from the list this modal already loaded — an invented id fails the
        server's "it must exist" check, and that failure reads as a bug in the form. Disabled
        until the roles arrive, or the button would leave the one required field empty.
      */
      autogenerate={{
        onGenerate: () => {
          if (roles.length > 0) setRoleId(roles[Math.floor(Math.random() * roles.length)].id);
        },
        disabled: saving || rolesLoading || roles.length === 0,
      }}
      confirm={{
        id: 'edit-user-role-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="edit-user-role-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="edit-user-role-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* One role per account, so this is a single Select — never a multi-picker. */}
        <Select
          id="edit-user-role-picker"
          label="Role"
          required
          remote
          loading={rolesLoading}
          description="Permissions come from the role. Changing it re-seeds this account's grants."
          value={roleId || null}
          onChange={setRoleId}
          options={roles.map((role) => ({
            value: role.id,
            label: role.name,
            right: `${role.permissionCount} perms`,
          }))}
          error={valid ? undefined : 'Select a role.'}
          disabled={saving}
        />

      </div>
    </Modal>
  );
}
